import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import OtpStep from "./OtpStep";
import logo from "../assets/images/Logo-New.png";
import "./Login.css";

const BENEFITS = [
  ["Track your orders", "See every order and its delivery status in one place."],
  ["Book consultations", "Reserve astrology, numerology and Vastu sessions with our consultants."],
  ["Use our free tools", "Check a mobile number or find the Rudraksha suited to you."],
];

const Login = () => {
  const [mode, setMode] = useState("login"); // "login" | "signup"

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Set when the password step passed but the mobile number still needs an OTP.
  const [challenge, setChallenge] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { login, signup, phoneOtpRequired } = useAuth();

  const redirectTo = location.state?.redirectTo || "/account";
  const intent = location.state?.intent || null;

  const finishLogin = () => {
    navigate(redirectTo, {
      state: { intent, ...(location.state?.carryState || {}) },
    });
  };

  const switchMode = (next) => {
    setMode(next);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email.trim() || !password.trim()) {
      setError("Please enter email and password.");
      return;
    }

    if (mode === "signup" && !name.trim()) {
      setError("Please enter your name.");
      return;
    }

    // The number is verified by OTP right after signup, so it is required.
    if (mode === "signup" && !/^(?:\+?91|0)?[6-9]\d{9}$/.test(phone.replace(/[\s-]/g, ""))) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }

    setLoading(true);

    try {
      const result =
        mode === "signup"
          ? await signup({
              email: email.trim(),
              password,
              name: name.trim(),
              phone: phone.trim(),
            })
          : await login(email.trim(), password);

      if (result.challenge) {
        setChallenge(result.challenge);
      } else {
        finishLogin();
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isSignup = mode === "signup";

  return (
    <div className="auth-page">
      <aside className="auth-brand">
        <Link to="/" className="auth-brand-logo" aria-label="Astro Logical Solutions home">
          <img src={logo} alt="Astro Logical Solutions" />
        </Link>
        <h1>
          Welcome to
          <br />
          Astro Logical™ Solutions
        </h1>
        <p className="auth-brand-lead">Authentic Rudraksha, gemstones and Vedic guidance, together in one account.</p>
        <ul className="auth-benefits">
          {BENEFITS.map(([title, text]) => (
            <li key={title}>
              <strong>{title}</strong>
              <span>{text}</span>
            </li>
          ))}
        </ul>
      </aside>

      <main className="auth-panel">
        <div className="customer-login-card">
          <Link to="/" className="auth-back">
            ← Back to website
          </Link>

          <header className="customer-login-logo">
            <h2>{challenge ? "Verify your mobile number" : isSignup ? "Create your account" : "Welcome back"}</h2>
            <p>
              {challenge
                ? "One last step to keep your account secure."
                : isSignup
                ? "Create an account to continue."
                : "Sign in to continue."}
            </p>
          </header>

          {challenge ? (
            <OtpStep
              challenge={challenge}
              onVerified={finishLogin}
              onBack={() => {
                setChallenge(null);
                setError("");
              }}
            />
          ) : (
            <>
              <div className="auth-tabs" role="tablist" aria-label="Login or create an account">
                {[
                  ["login", "Login"],
                  ["signup", "Create account"],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={mode === key}
                    className={`auth-tab ${mode === key ? "is-active" : ""}`}
                    onClick={() => switchMode(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {error && (
                <div className="customer-error-alert" role="alert">
                  {error}
                </div>
              )}

              <form className="customer-login-form" onSubmit={handleSubmit} noValidate>
                {isSignup && (
                  <div className="form-group">
                    <label htmlFor="auth-name">Full name</label>
                    <input
                      id="auth-name"
                      type="text"
                      autoComplete="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your full name"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="auth-email">Email</label>
                  <input
                    id="auth-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                  />
                </div>

                {isSignup && (
                  <div className="form-group">
                    <label htmlFor="auth-phone">Mobile number</label>
                    <input
                      id="auth-phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="9876543210"
                    />
                    <span className="form-hint">
                      {phoneOtpRequired
                        ? "We will text you a one-time password to verify it."
                        : "We use it for order and delivery updates."}
                    </span>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="auth-password">Password</label>
                  <div className="auth-password">
                    <input
                      id="auth-password"
                      type={showPassword ? "text" : "password"}
                      autoComplete={isSignup ? "new-password" : "current-password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      className="auth-password-toggle"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      aria-pressed={showPassword}
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                <button type="submit" className="customer-login-btn" disabled={loading}>
                  {loading ? "Please wait..." : isSignup ? "Create account" : "Login"}
                </button>
              </form>

              <p className="auth-legal">
                By continuing you agree to our <Link to="/terms-and-conditions">Terms &amp; Conditions</Link> and{" "}
                <Link to="/privacy-policy">Privacy Policy</Link>.
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Login;
